import { useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { FileDown, Loader2, Package } from "lucide-react";
import jsPDF from "jspdf";
import JSZip from "jszip";

/**
 * Bulk PDF invoice export.
 * Iterates the /print/txn/:id URL in a hidden iframe for each invoice,
 * captures with html2canvas → PDF, packages into a ZIP.
 */
export default function BulkExport() {
  const [range, setRange] = useState({ frm: "", to: "" });
  const [txnType, setTxnType] = useState("sale");
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0, current: "" });

  const runExport = async () => {
    setBusy(true);
    setProgress({ done: 0, total: 0, current: "" });
    try {
      const params = new URLSearchParams({ txn_type: txnType, limit: 500 });
      if (range.frm) params.set("frm", range.frm);
      if (range.to) params.set("to", range.to);
      const { data: txns } = await api.get(`/txns?${params}`);
      if (!txns.length) { toast.error("No invoices in range"); setBusy(false); return; }
      const firmRes = await api.get("/firms/me");
      const firm = firmRes.data;

      setProgress({ done: 0, total: txns.length, current: "" });
      const zip = new JSZip();

      for (let i = 0; i < txns.length; i++) {
        const t = txns[i];
        setProgress({ done: i, total: txns.length, current: t.number });
        let party = null;
        if (t.party_id) {
          try { party = (await api.get(`/parties/${t.party_id}`)).data; } catch {}
        }
        const pdf = renderInvoicePdf(t, firm, party);
        zip.file(`${t.number}.pdf`, pdf.output("blob"));
      }

      const blob = await zip.generateAsync({ type: "blob" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `invoices_${txnType}_${range.frm || "all"}_${range.to || "all"}.zip`;
      a.click();
      URL.revokeObjectURL(url);

      toast.success(`Exported ${txns.length} PDFs`);
      setProgress({ done: txns.length, total: txns.length, current: "" });
    } catch (e) {
      console.error(e);
      toast.error("Export failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div className="overline">Bulk Export</div>
      <h1 className="text-3xl font-bold tracking-tight mb-6">Download all invoices as PDF</h1>

      <div className="bg-white border border-border rounded-lg p-6 max-w-2xl">
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label>Document type</Label>
            <select value={txnType} onChange={(e) => setTxnType(e.target.value)}
              className="w-full border border-border rounded-md h-9 px-2 bg-white" data-testid="bulk-type">
              <option value="sale">Sales</option>
              <option value="pos">POS</option>
              <option value="purchase">Purchases</option>
              <option value="estimate">Estimates</option>
              <option value="proforma">Proforma</option>
              <option value="credit_note">Credit Notes</option>
              <option value="debit_note">Debit Notes</option>
              <option value="delivery_challan">Delivery Challans</option>
            </select>
          </div>
          <div>
            <Label>From</Label>
            <Input type="date" value={range.frm} onChange={(e) => setRange({ ...range, frm: e.target.value })} data-testid="bulk-from" />
          </div>
          <div>
            <Label>To</Label>
            <Input type="date" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} data-testid="bulk-to" />
          </div>
        </div>
        <div className="mt-4 flex items-center gap-3">
          <Button className="btn-primary" onClick={runExport} disabled={busy} data-testid="bulk-run">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <FileDown size={14} />}
            {busy ? "Generating..." : "Generate PDF ZIP"}
          </Button>
          {progress.total > 0 && (
            <span className="text-sm text-muted-foreground">
              <Package size={12} className="inline mr-1" />
              {progress.done}/{progress.total}
              {progress.current ? ` · ${progress.current}` : ""}
            </span>
          )}
        </div>
        <div className="text-xs text-muted-foreground mt-3">
          Each invoice is rendered directly to A4 PDF (no browser dialogs) and packaged into a single ZIP. Great for month-end backups and audit.
        </div>
      </div>
    </div>
  );
}

function fmtINR(n) {
  return "INR " + Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function renderInvoicePdf(t, firm, party) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const W = doc.internal.pageSize.getWidth();
  let y = 40;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor("#666");
  doc.text("ANV ASSOCIATE", 40, y);
  doc.setFontSize(18);
  doc.setTextColor("#111");
  doc.text(firm.firm_name || "AccountifyPro", 40, y + 18);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text(firm.address || "", 40, y + 34);
  doc.text(`${firm.state || ""} · ${firm.contact || ""} · ${firm.email || ""}`, 40, y + 46);
  if (firm.gstin) doc.text(`GSTIN: ${firm.gstin}`, 40, y + 58);

  const title = ({
    sale: "TAX INVOICE", pos: "POS RECEIPT", purchase: "PURCHASE BILL",
    estimate: "QUOTATION", proforma: "PROFORMA INVOICE",
    credit_note: "CREDIT NOTE", debit_note: "DEBIT NOTE",
    delivery_challan: "DELIVERY CHALLAN",
  })[t.txn_type] || t.txn_type.toUpperCase();
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.text(title, W - 40, y + 8, { align: "right" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text(`No: ${t.number}`, W - 40, y + 24, { align: "right" });
  doc.text(`Date: ${new Date(t.date).toLocaleDateString()}`, W - 40, y + 36, { align: "right" });

  y += 78;
  doc.setDrawColor(150);
  doc.line(40, y, W - 40, y);
  y += 16;

  // Party
  doc.setFont("helvetica", "bold");
  doc.text("BILL TO", 40, y);
  doc.setFont("helvetica", "normal");
  y += 12;
  doc.text(party?.name || t.party_name || "Walk-in", 40, y); y += 12;
  if (party?.address) { doc.text(party.address.slice(0, 90), 40, y); y += 12; }
  if (party?.phone) { doc.text(party.phone, 40, y); y += 12; }
  if (party?.gstin) { doc.text(`GSTIN: ${party.gstin}`, 40, y); y += 12; }
  y += 6;

  // Vehicle
  if (t.vehicle_number || t.driver_name) {
    doc.setFont("helvetica", "bold"); doc.text("DELIVERY", 40, y); doc.setFont("helvetica", "normal"); y += 12;
    doc.text(`Vehicle: ${t.vehicle_number || "-"} · Driver: ${t.driver_name || "-"} · KM: ${t.km || 0}`, 40, y); y += 12;
    if (t.delivery_address) { doc.text(t.delivery_address.slice(0, 90), 40, y); y += 12; }
    y += 4;
  }

  // Line items header
  doc.setFillColor(240);
  doc.rect(40, y, W - 80, 18, "F");
  doc.setFont("helvetica", "bold");
  doc.text("#", 45, y + 12);
  doc.text("Item", 65, y + 12);
  doc.text("Qty", 300, y + 12);
  doc.text("Rate", 360, y + 12);
  doc.text("GST%", 420, y + 12);
  doc.text("Amount", W - 45, y + 12, { align: "right" });
  y += 22;
  doc.setFont("helvetica", "normal");

  t.lines.forEach((l, i) => {
    if (y > 720) { doc.addPage(); y = 40; }
    const amt = l.qty * l.price - (l.discount || 0);
    doc.text(String(i + 1), 45, y);
    doc.text(String(l.name || "").slice(0, 45), 65, y);
    doc.text(String(l.qty), 300, y);
    doc.text(fmtINR(l.price), 360, y);
    doc.text(`${l.gst_rate || 0}%`, 420, y);
    doc.text(fmtINR(amt), W - 45, y, { align: "right" });
    y += 14;
  });

  y += 8;
  doc.line(40, y, W - 40, y);
  y += 14;

  const rows = [
    ["Subtotal", t.subtotal],
    ["Discount", -t.total_discount],
    ["CGST", t.cgst],
    ["SGST", t.sgst],
    ["IGST", t.igst],
  ].filter(([, v]) => v);
  rows.forEach(([k, v]) => {
    doc.text(k, W - 200, y); doc.text(fmtINR(v), W - 45, y, { align: "right" }); y += 12;
  });
  doc.setFont("helvetica", "bold");
  doc.text("TOTAL", W - 200, y); doc.text(fmtINR(t.total), W - 45, y, { align: "right" });
  y += 16;
  doc.setFont("helvetica", "normal");
  doc.text(`Paid (${t.payment_mode || "-"}): ${fmtINR(t.paid)}`, W - 200, y); y += 12;
  doc.text(`Balance: ${fmtINR(t.balance)}`, W - 200, y);

  y += 40;
  doc.setFontSize(8); doc.setTextColor("#666");
  doc.text(`Thank you for your business! · Developed by ANV ASSOCIATE · Helpline 8446463026`, W / 2, y, { align: "center" });

  return doc;
}
